import { TFunction } from 'i18next'
import { UseFormReturn } from 'react-hook-form'
import { FieldType, IFormConfig } from 'types'
import { Signer, signerSchema } from './signer-schema'
import { PhoneNumberField } from 'components/PhoneNumberField'
import { CountryMultiSelect } from 'components/CountryMultiSelect'
import { AddressFields } from 'components/AddressFields'
import { FullNameFields } from 'components/FullNameFields'
import { CountryAlpha2 } from 'generated/graphql'

const identificationTypeOptions = [
  { value: 'itin', label: 'ITIN' },
  { value: 'passport', label: 'Passport' }
]

export const getSignerModalConfig = (t: TFunction): IFormConfig<Signer> => ({
  schema: signerSchema,
  fields: [
    {
      name: 'individual.firstName',
      type: FieldType.CUSTOM,
      label: t('individuals.signer.fields.firstName.label'),
      tooltip: t('individuals.signer.fields.firstName.tooltip'),
      CustomComponent: FullNameFields,
      customComponentProps: {
        fields: [
          {
            name: 'individual.firstName',
            label: t('individuals.signer.fields.firstName.label'),
            placeholder: t('individuals.signer.fields.firstName.placeholder')
          },
          {
            name: 'individual.middleName',
            label: t('individuals.signer.fields.middleName.label'),
            placeholder: t('individuals.signer.fields.middleName.placeholder')
          },
          {
            name: 'individual.lastName',
            label: t('individuals.signer.fields.lastName.label'),
            placeholder: t('individuals.signer.fields.lastName.placeholder')
          }
        ]
      }
    },
    {
      name: 'relation.title',
      label: t('individuals.signer.fields.title.label'),
      placeholder: t('individuals.signer.fields.title.placeholder'),
      tooltip: t('individuals.signer.fields.title.tooltip')
    },
    {
      name: 'individual.dateOfBirth',
      type: FieldType.DATE,
      label: t('individuals.signer.fields.dateOfBirth.label'),
      placeholder: 'DD/MM/YYYY',
      tooltip: t('individuals.signer.fields.dateOfBirth.tooltip')
    },
    {
      name: 'individual.nationalities',
      type: FieldType.CUSTOM,
      label: t('individuals.signer.fields.nationality.label'),
      placeholder: t('individuals.signer.fields.nationality.placeholder'),
      tooltip: t('individuals.signer.fields.nationality.tooltip'),
      CustomComponent: CountryMultiSelect,
      customComponentProps: {
        onChange: (values: string[], methods: UseFormReturn<Signer>) => {
          if (values.includes(CountryAlpha2.Us)) {
            methods.setValue('individual.identificationDocumentType', '')
            methods.setValue('individual.idNumber', '')
          } else {
            methods.setValue('individual.ssn', '')
          }
        }
      }
    },
    {
      name: 'individual.ssn',
      format: '###-##-####',
      label: t('individuals.signer.fields.ssn.label'),
      placeholder: t('individuals.signer.fields.ssn.placeholder'),
      tooltip: t('individuals.signer.fields.ssn.tooltip'),
      ignoreField: methods =>
        !methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.identificationDocumentType',
      type: FieldType.SELECT,
      label: t('individuals.signer.fields.identificationDocumentType.label'),
      placeholder: t(
        'individuals.signer.fields.identificationDocumentType.placeholder'
      ),
      tooltip: t(
        'individuals.signer.fields.identificationDocumentType.tooltip'
      ),
      options: identificationTypeOptions,
      ignoreField: methods =>
        methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.idNumber',
      label: t('individuals.signer.fields.idNumber.label'),
      placeholder: t('individuals.signer.fields.idNumber.placeholder'),
      tooltip: t('individuals.signer.fields.idNumber.tooltip'),
      ignoreField: methods =>
        methods.watch('individual.nationalities')?.includes(CountryAlpha2.Us)
    },
    {
      name: 'individual.residentialAddress.addressLine',
      type: FieldType.CUSTOM,
      label: t('individuals.signer.fields.addressLine.label'),
      tooltip: t('individuals.signer.fields.addressLine.tooltip'),
      CustomComponent: AddressFields,
      customComponentProps: {
        withCountry: true,
        country: CountryAlpha2.Us,
        placeholders: {
          addressLine: t('individuals.signer.fields.addressLine.placeholder'),
          addressLine2: t('individuals.signer.fields.addressLine2.placeholder'),
          city: t('individuals.signer.fields.city.placeholder'),
          state: t('individuals.signer.fields.state.placeholder'),
          zip: t('individuals.signer.fields.zip.placeholder')
        }
      }
    },
    {
      name: 'individual.email',
      label: t('individuals.signer.fields.email.label'),
      placeholder: t('individuals.signer.fields.email.placeholder'),
      tooltip: t('individuals.signer.fields.email.tooltip')
    },
    {
      name: 'individual.phoneNumber',
      type: FieldType.CUSTOM,
      label: t('individuals.signer.fields.phoneNumber.label'),
      tooltip: t('individuals.signer.fields.phoneNumber.tooltip'),
      CustomComponent: PhoneNumberField
    }
  ]
})
